import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";

// Use CDN worker matching the installed pdfjs-dist version (CRA-compatible — no ?url import).
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

/**
 * Canvas-based PDF preview using PDF.js. Works in every browser without
 * relying on the native PDF plugin (which is disabled in many environments).
 * Receives a blob URL and renders every page stacked vertically.
 */
export default function PDFViewer({ url, testId = "pdf-viewer", height = "70vh" }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setNumPages(0);
    const container = containerRef.current;
    if (container) container.innerHTML = "";

    (async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({ url });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        setNumPages(pdf.numPages);
        const containerWidth = container?.clientWidth || 900;
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNum);
          const baseViewport = page.getViewport({ scale: 1 });
          const scale = Math.max(1, Math.min(2.2, containerWidth / baseViewport.width));
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = "100%";
          canvas.style.height = "auto";
          canvas.style.display = "block";
          canvas.style.marginBottom = "8px";
          canvas.style.background = "#fff";
          canvas.style.borderRadius = "4px";
          canvas.style.boxShadow = "0 1px 4px rgba(0,0,0,0.25)";
          container?.appendChild(canvas);
          const ctx = canvas.getContext("2d");
          await page.render({ canvasContext: ctx, viewport }).promise;
        }
        if (!cancelled) setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "No se pudo renderizar el PDF");
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [url]);

  return (
    <div data-testid={testId} style={{ width: "100%" }}>
      {loading && (
        <div className="flex items-center justify-center text-dim" style={{ height: "20vh" }} data-testid={`${testId}-loading`}>
          <span className="spinner" /> &nbsp; Renderizando PDF…
        </div>
      )}
      {error && (
        <div className="card" style={{ background: "rgba(229,57,53,0.10)", border: "1px solid var(--danger)" }} data-testid={`${testId}-error`}>
          {error}
        </div>
      )}
      <div
        ref={containerRef}
        data-testid={`${testId}-canvas-container`}
        style={{
          width: "100%",
          height,
          overflowY: "auto",
          padding: 12,
          background: "var(--bg-3, #0f1924)",
          border: "1px solid var(--border-2)",
          borderRadius: 6,
        }}
      />
      {!loading && !error && numPages > 0 && (
        <div className="text-xs text-dim mt-2" data-testid={`${testId}-pages`}>
          {numPages} página{numPages === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}
